import { useMemo, useState } from "react";
import { Loader2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { strings } from "@/i18n/strings";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { field } from "@/components/admin/SettingsPanel";

const GROUPS = {
  nav: "Menu", cta: "Buttons", hero: "Home — Top", marquee: "Home — Scrolling text", choice: "Home — Two Roads", featured: "Home — Recent Messages",
  schedule: "Home — Schedule", give: "Home — Give section", about: "About page", sermons: "Messages page", cat: "Message categories",
  audio: "Player messages", live: "Player messages", donate: "Give page", contact: "Contact page", footer: "Footer", player: "Player messages",
  ann: "Home — Announcements", pay: "Thank-you / payment pages",
};

export const TextPanel = () => {
  const { settings, setSettings } = useSite();
  const [vals, setVals] = useState(() => ({ en: { ...(settings.text?.en || {}) }, mh: { ...(settings.text?.mh || {}) } }));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [mhOnly, setMhOnly] = useState(false);
  const [needsWork, setNeedsWork] = useState(false);
  const valOf = (lang, k) => vals[lang][k] ?? strings[lang]?.[k] ?? "";
  const isDraft = (k) => { const m = valOf("mh", k).trim(); return !m || m.startsWith("[") || m === valOf("en", k).trim(); };

  const grouped = useMemo(() => {
    const s = q.toLowerCase();
    const out = {};
    Object.keys(strings.en).forEach((k) => {
      const hay = `${k} ${strings.en[k]} ${strings.mh[k] || ""} ${vals.en[k] || ""} ${vals.mh[k] || ""}`.toLowerCase();
      if (s && !hay.includes(s)) return;
      if (needsWork && !isDraft(k)) return;
      const g = GROUPS[k.split(".")[0]] || "Other";
      (out[g] = out[g] || []).push(k);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, vals, needsWork]);
  const draftCount = Object.keys(strings.en).filter(isDraft).length;

  const val = (lang, k) => vals[lang][k] ?? strings[lang]?.[k] ?? "";
  const set = (lang, k) => (e) => setVals((p) => ({ ...p, [lang]: { ...p[lang], [k]: e.target.value } }));

  const save = async () => {
    setBusy(true);
    const diff = (lang) => Object.fromEntries(Object.entries(vals[lang]).filter(([k, v]) => v !== (strings[lang]?.[k] ?? "")));
    try {
      const { data } = await api.put("/settings/text", { en: diff("en"), mh: diff("mh") });
      if (data?.pending) {
        toast("Sent for approval — it will appear once a Super Admin approves it");
      } else {
        setSettings((p) => ({ ...p, ...data }));
        toast.success("Text saved — live on the site");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="text-panel">
      <div className="sticky top-0 z-10 -mx-1 flex flex-col gap-3 bg-[#0B111E]/90 px-1 py-3 backdrop-blur-md sm:flex-row">
        <label className="flex flex-1 items-center gap-2 rounded-full border border-[#F4EEE2]/15 px-4 py-2.5">
          <Search className="h-4 w-4 text-[#B7BFCC]" />
          <input data-testid="text-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find text to edit…" className="w-full bg-transparent text-sm text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4]" />
        </label>
        <button data-testid="text-save-btn" onClick={save} disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all text
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button data-testid="text-mode-mh" onClick={() => setMhOnly((v) => !v)} className={`rounded-full px-4 py-2 font-ui text-xs font-semibold ${mhOnly ? "bg-[#D4AF37] text-[#0B111E]" : "border border-[#F4EEE2]/15 text-[#F4EEE2]"}`}>
          {mhOnly ? "✓ " : ""}Marshallese editing mode
        </button>
        <button data-testid="text-needs-work" onClick={() => setNeedsWork((v) => !v)} className={`rounded-full px-4 py-2 font-ui text-xs font-semibold ${needsWork ? "bg-[#D4AF37] text-[#0B111E]" : "border border-[#F4EEE2]/15 text-[#F4EEE2]"}`}>
          {needsWork ? "✓ " : ""}Show only Marshallese that needs work ({draftCount})
        </button>
        <span className="text-xs text-[#9AA4B4]">Tip: Messages, announcements, pages and the schedule each have their own Marshallese boxes in their tabs.</span>
      </div>
      {Object.entries(grouped).map(([g, keys]) => (
        <section key={g} className="mt-8">
          <h3 className="font-display text-2xl text-[#D4AF37]">{g}</h3>
          <div className="mt-3 grid gap-4">
            {keys.map((k) => (
              <div key={k} data-testid={`text-row-${k}`} className={`grid gap-2 border-b border-[#F4EEE2]/10 pb-4 ${mhOnly ? "" : "md:grid-cols-2"}`}>
                {mhOnly ? (
                  <p className="text-sm text-[#4A5568]"><span className="mr-2 font-mono text-[10px] uppercase tracking-widest text-[#9AA4B4]">English:</span>{val("en", k)}</p>
                ) : (
                <label className="block">
                  <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-[#9AA4B4]">{k} · English</span>
                  <textarea data-testid={`text-en-${k}`} rows={val("en", k).length > 70 ? 3 : 1} value={val("en", k)} onChange={set("en", k)} className={field} />
                </label>
                )}
                <label className="block">
                  <span className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#9AA4B4]">Marshallese {isDraft(k) && <span className="rounded-full bg-[#D4AF37]/20 px-2 py-0.5 text-[#8A6A12]">needs work</span>}</span>
                  <textarea data-testid={`text-mh-${k}`} rows={val("mh", k).length > 70 || mhOnly ? 2 : 1} value={val("mh", k)} onChange={set("mh", k)} className={field} />
                </label>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
